import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BookOpen,
  Library,
  Clapperboard,
  ImageIcon,
  FileText,
  HelpCircle,
  ChevronRight,
  CheckCircle,
  Lightbulb,
  AlertCircle,
  ArrowLeft,
  BookMarked,
  Clock,
  Headphones
} from "lucide-react";

// Helper function to check if we're in preview environment
// AUDIOBOOK RE-ENABLED FOR PRODUCTION - user testing with upgraded resources
const isPreviewEnvironment = () => {
  return true; // Always show audiobook now
};

const HelpCenter = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("books");
  const [showAudiobook, setShowAudiobook] = useState(false);

  useEffect(() => {
    setShowAudiobook(isPreviewEnvironment());
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  const features = [
    { id: "books", label: "Book Generation", icon: BookOpen },
    { id: "series", label: "Multi-Book Series", icon: Library },
    { id: "ebook", label: "Ebook Formatter", icon: BookMarked },
    ...(showAudiobook ? [{ id: "audiobook", label: "Audiobook Creator", icon: Headphones }] : []),
    { id: "scripts", label: "Script Creator", icon: Clapperboard },
    { id: "covers", label: "Cover Creator", icon: ImageIcon },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-stone-50 to-neutral-100 flex">
      <Sidebar onLogout={handleLogout} />
      
      <div className="flex-1 ml-0 md:ml-64 py-6 md:py-12 px-4 md:px-8">
        <div className="container mx-auto max-w-5xl">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <HelpCircle className="h-10 w-10 text-amber-600" />
              <div>
                <h1 className="text-3xl md:text-4xl font-bold playfair text-stone-900">Help Center</h1>
                <p className="text-stone-600">Learn how to use every feature of DTR Creative</p>
              </div>
            </div>
          </div>

          {/* Feature Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 h-auto bg-transparent">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <TabsTrigger
                    key={feature.id}
                    value={feature.id}
                    className="flex items-center gap-2 px-3 py-2 data-[state=active]:bg-amber-100 data-[state=active]:text-amber-900 rounded-lg border text-xs sm:text-sm"
                  >
                    <Icon className="h-4 w-4" />
                    <span className="hidden lg:inline">{feature.label}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {/* Book Generation Guide */}
            <TabsContent value="books" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-6 w-6 text-amber-600" />
                    Book Generation Guide
                  </CardTitle>
                  <CardDescription>Create AI-generated books from your ideas</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Overview */}
                  <div className="bg-amber-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                      <Lightbulb className="h-5 w-5" />
                      What is Book Generation?
                    </h3>
                    <p className="text-amber-800">
                      Transform your book ideas into fully written manuscripts. Our AI writes complete chapters 
                      based on your title, description, genre, and preferences. Each book costs 1 credit.
                    </p>
                  </div>

                  {/* Steps */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-stone-900">Step-by-Step Guide</h3>
                    
                    <div className="space-y-3">
                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Choose Your Content Mode</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            <strong>Generate:</strong> AI creates the story from your description<br/>
                            <strong>Import:</strong> Upload an existing document (.txt, .docx, .pdf) to use as a base
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Enter Book Details</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            • <strong>Title:</strong> Your book's title<br/>
                            • <strong>Description:</strong> A detailed summary of what your book is about (the more detail, the better!)<br/>
                            • <strong>Genre:</strong> Fiction, Non-Fiction, Romance, Mystery, etc.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Configure Options</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            • <strong>Number of Chapters:</strong> 10-30 chapters recommended<br/>
                            • <strong>Words per Chapter:</strong> 2,500-4,000 words is standard<br/>
                            • <strong>Writing Style:</strong> Descriptive, Fast-paced, Literary, etc.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-amber-50 border-2 border-amber-300 rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">4</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Preview Before Generating ⚠️</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            <strong>Important:</strong> Click "Preview" to see a sample of how your book will be written. 
                            This shows you the AI's writing style based on your settings.<br/><br/>
                            <strong>Read the preview carefully!</strong> If you don't like how it reads, go back and adjust 
                            your writing style or description. Once you click "Generate," your credit is spent and cannot be refunded.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">5</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Generate & Wait</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            Once you're happy with the preview, click "Generate Book" and wait. Generation typically takes 5-15 minutes depending on book length.
                            You can leave the page - generation continues in the background.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">6</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Review & Edit</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            Once complete, review each chapter. You can edit any chapter or regenerate specific ones if needed.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tips */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      Pro Tips
                    </h3>
                    <ul className="text-blue-800 text-sm space-y-1">
                      <li>• <strong>Always use Preview first!</strong> - Credits are non-refundable once you generate</li>
                      <li>• Write detailed descriptions - the AI uses this to shape the entire story</li>
                      <li>• Include character names and key plot points in your description</li>
                      <li>• Start with 15-20 chapters for a standard novel length</li>
                      <li>• If generation stalls, use the "Fix and Resume" button</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Multi-Book Series Guide */}
            <TabsContent value="series" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Library className="h-6 w-6 text-amber-600" />
                    Multi-Book Series Guide
                  </CardTitle>
                  <CardDescription>Create connected book series with consistent characters and storylines</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Overview */}
                  <div className="bg-amber-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                      <Lightbulb className="h-5 w-5" />
                      What is Multi-Book Series?
                    </h3>
                    <p className="text-amber-800">
                      Create a series of connected books that share the same characters, settings, and storyline.
                      The AI maintains continuity across all books in your series. Perfect for trilogies, sagas, or ongoing series.
                    </p>
                  </div>

                  {/* Steps */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-stone-900">Step-by-Step Guide</h3>
                    
                    <div className="space-y-3">
                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Create Your Series</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            Click "New Series" and give your series a name and description. This sets the overall theme for all books.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Define Characters</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            Add recurring characters with names, descriptions, and roles. These characters will appear consistently across all books in the series.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Add Settings</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            Define locations and worlds that appear in your series. This ensures consistent world-building across books.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-amber-50 border-2 border-amber-300 rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">4</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Preview Before Generating ⚠️</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            <strong>Important:</strong> Before generating each book, click "Preview" to see a sample of how the AI will write it.
                            This lets you check the writing style matches your vision.<br/><br/>
                            <strong>Review carefully!</strong> If the preview doesn't read the way you'd like, go back and adjust the writing style 
                            or book description. Once you click "Generate," your credit is spent and cannot be refunded.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">5</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Generate Book 1</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            Once you're happy with the preview, create your first book with a title and premise specific to Book 1. The AI will use your series setup to write it.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">6</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Continue the Series</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            Add more books to the series. You can set time jumps between books (e.g., "5 years later") to advance the timeline.
                            <strong> Remember to preview each new book before generating!</strong>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tips */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      Pro Tips
                    </h3>
                    <ul className="text-blue-800 text-sm space-y-1">
                      <li>• <strong>Always preview each book before generating!</strong> - Credits are non-refundable</li>
                      <li>• Plan your character arcs before starting - add notes about how they change</li>
                      <li>• Use the "Story Bible" feature to track important events</li>
                      <li>• Each book in a series costs 1 credit</li>
                      <li>• You can add new characters as the series progresses</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Ebook Formatter Guide */}
            <TabsContent value="ebook" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookMarked className="h-6 w-6 text-amber-600" />
                    Ebook Formatter Guide
                  </CardTitle>
                  <CardDescription>Create professional EPUB ebooks from your books</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Overview */}
                  <div className="bg-amber-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                      <Lightbulb className="h-5 w-5" />
                      What is Ebook Formatter?
                    </h3>
                    <p className="text-amber-800">
                      Convert your completed books into professional EPUB ebook files. The ebook includes your cover art, 
                      a clickable table of contents, properly formatted chapters with navigation links, and is ready for 
                      distribution on all major ebook platforms.
                    </p>
                  </div>

                  {/* Steps */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-stone-900">Step-by-Step Guide</h3>
                    
                    <div className="space-y-3">
                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Choose Your Book Source</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            <strong>DTR Book:</strong> Select any completed book from your DTR library<br/>
                            <strong>External Content:</strong> Paste text content from an external source
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Select Your Cover</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            <strong>DTR Cover:</strong> Choose a cover you've created in Cover Creator<br/>
                            <strong>Upload Cover:</strong> Upload your own cover image (PNG or JPG)<br/>
                            <strong>No Cover:</strong> Create an ebook without cover art
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Review Summary</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            Review your selections and confirm the ebook title. You can edit the title before creating.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">4</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Create Ebook</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            Click "Create Ebook" and wait a few seconds. Your EPUB will be generated with:
                            <br/>• Cover page (if selected)
                            <br/>• Clickable Table of Contents
                            <br/>• Formatted chapters with "Back to TOC" navigation
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">5</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Download</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            Once complete, click "Download EPUB" to save your ebook. The file can be opened in any ebook reader 
                            or uploaded to platforms like Amazon Kindle, Apple Books, Kobo, and more.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-green-900 mb-2">Pricing</h3>
                    <ul className="text-green-800 text-sm space-y-1">
                      <li>• <strong>Create Ebook:</strong> 1 credit per ebook</li>
                    </ul>
                  </div>

                  {/* Tips */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      Pro Tips
                    </h3>
                    <ul className="text-blue-800 text-sm space-y-1">
                      <li>• Use a high-quality cover image for best results (at least 1600x2560 pixels)</li>
                      <li>• The Table of Contents links are clickable - readers can jump to any chapter</li>
                      <li>• Each chapter has a "Back to Table of Contents" link for easy navigation</li>
                      <li>• Test your EPUB in a reader app before publishing</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Audiobook Creator Guide - Only shown when feature flag is enabled */}
            {showAudiobook && (
              <TabsContent value="audiobook" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Headphones className="h-6 w-6 text-amber-600" />
                      Audiobook Creator Guide
                    </CardTitle>
                    <CardDescription>Transform your books into professional audiobooks</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Overview */}
                    <div className="bg-amber-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                        <Lightbulb className="h-5 w-5" />
                        What is Audiobook Creator?
                      </h3>
                      <p className="text-amber-800">
                        Convert your completed DTR books or external content into high-quality audiobooks using 
                        AI-powered text-to-speech. Choose from 9 different voices and add your book cover as album art.
                        Each audiobook costs 3 credits.
                      </p>
                    </div>

                    {/* Cost & Time Info */}
                    <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                      <h3 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                        <Clock className="h-5 w-5" />
                        Cost & Generation Time
                      </h3>
                      <ul className="text-yellow-800 space-y-1 text-sm">
                        <li>• <strong>Cost:</strong> 3 credits per audiobook</li>
                        <li>• <strong>Time:</strong> Up to 5 hours depending on book length</li>
                        <li>• <strong>Speed:</strong> Approximately 8 chapters per hour</li>
                        <li>• Generation continues on the server even if you close your browser</li>
                      </ul>
                    </div>

                    {/* Steps */}
                    <div>
                      <h3 className="font-semibold text-stone-900 mb-4">How to Create an Audiobook</h3>
                      <div className="space-y-4">
                        <div className="flex gap-4">
                          <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                          <div>
                            <h4 className="font-medium text-stone-900">Go to Audiobook Creator</h4>
                            <p className="text-stone-600 text-sm">Click "Audiobook Creator" in the sidebar menu</p>
                          </div>
                        </div>
                        <div className="flex gap-4">
                          <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                          <div>
                            <h4 className="font-medium text-stone-900">Click "New Audiobook"</h4>
                            <p className="text-stone-600 text-sm">This opens the audiobook creation wizard</p>
                          </div>
                        </div>
                        <div className="flex gap-4">
                          <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                          <div>
                            <h4 className="font-medium text-stone-900">Choose Your Content Source</h4>
                            <p className="text-stone-600 text-sm">Select a completed DTR book or paste external content</p>
                          </div>
                        </div>
                        <div className="flex gap-4">
                          <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">4</div>
                          <div>
                            <h4 className="font-medium text-stone-900">Select Cover Art (Optional)</h4>
                            <p className="text-stone-600 text-sm">Choose a DTR cover or upload your own image for album art</p>
                          </div>
                        </div>
                        <div className="flex gap-4">
                          <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">5</div>
                          <div>
                            <h4 className="font-medium text-stone-900">Choose Voice & Settings</h4>
                            <p className="text-stone-600 text-sm">Pick from 9 AI voices (Nova, Alloy, Echo, Fable, Onyx, Shimmer, Ash, Sage, Coral)</p>
                          </div>
                        </div>
                        <div className="flex gap-4">
                          <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">6</div>
                          <div>
                            <h4 className="font-medium text-stone-900">Create & Wait</h4>
                            <p className="text-stone-600 text-sm">Click "Create Audiobook" and wait for generation to complete</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Tips */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                        <CheckCircle className="h-5 w-5" />
                        Pro Tips
                      </h3>
                      <ul className="text-blue-800 space-y-1 text-sm">
                        <li>• <strong>Refresh the page</strong> to see updated progress</li>
                        <li>• Generation continues on the server even if you close your browser</li>
                        <li>• Use the "Check Stuck" button if progress seems stalled</li>
                        <li>• HD Audio Quality provides the best listening experience</li>
                        <li>• Books up to 40 chapters are supported</li>
                      </ul>
                    </div>

                    {/* Troubleshooting */}
                    <div className="bg-red-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                        <AlertCircle className="h-5 w-5" />
                        Troubleshooting
                      </h3>
                      <ul className="text-red-800 space-y-1 text-sm">
                        <li>• <strong>Stuck on "Processing":</strong> Click the "Check Stuck" button to diagnose</li>
                        <li>• <strong>No progress:</strong> Refresh the page - generation happens server-side</li>
                        <li>• <strong>Download not working:</strong> Wait for all chapters to complete first</li>
                        <li>• <strong>Need help?</strong> Contact Support@dtrcreative.com</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* Script Creator Guide */}
            <TabsContent value="scripts" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clapperboard className="h-6 w-6 text-amber-600" />
                    Script Creator Guide
                  </CardTitle>
                  <CardDescription>Convert your books into Hollywood-format screenplays</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Overview */}
                  <div className="bg-amber-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                      <Lightbulb className="h-5 w-5" />
                      What is Script Creator?
                    </h3>
                    <p className="text-amber-800">
                      Transform your novels into properly formatted screenplays ready for film or TV production.
                      The AI adapts your story into scenes with dialogue, action lines, and industry-standard formatting.
                    </p>
                  </div>

                  {/* Steps */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-stone-900">Step-by-Step Guide</h3>
                    
                    <div className="space-y-3">
                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Choose Your Source</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            <strong>DTR Book:</strong> Select a book you've already generated in DTR<br/>
                            <strong>Upload:</strong> Upload an external book file (.txt, .docx, .pdf)
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Select Scene Count</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            • <strong>30 scenes:</strong> ~90 pages, shorter film<br/>
                            • <strong>60 scenes:</strong> ~120 pages, standard feature film<br/>
                            • <strong>80 scenes:</strong> ~150+ pages, epic/longer film
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Choose Adaptation Style</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            <strong>Faithful:</strong> Preserves all major plot points and character arcs<br/>
                            <strong>Condensed:</strong> Focuses on main plot, streamlines subplots
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">4</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Generate & Wait</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            Screenplay generation takes 10-30 minutes depending on scene count. 
                            You'll see progress updates as each scene is written.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">5</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Download & Edit</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            Once complete, download your screenplay as a PDF in proper format.
                            You can view individual scenes and make edits before exporting.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-green-900 mb-2">Pricing</h3>
                    <ul className="text-green-800 text-sm space-y-1">
                      <li>• <strong>From DTR Book:</strong> 5 credits (includes combo discount)</li>
                      <li>• <strong>From Upload:</strong> 5 credits</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Cover Creator Guide */}
            <TabsContent value="covers" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="h-6 w-6 text-amber-600" />
                    Cover Creator Guide
                  </CardTitle>
                  <CardDescription>Generate professional book covers with AI</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Overview */}
                  <div className="bg-amber-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                      <Lightbulb className="h-5 w-5" />
                      What is Cover Creator?
                    </h3>
                    <p className="text-amber-800">
                      Generate stunning book covers including front cover, back cover, and spine.
                      The AI creates 4 unique design variations to choose from, optimized for print and digital publishing.
                    </p>
                  </div>

                  {/* Steps */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg text-stone-900">Step-by-Step Guide</h3>
                    
                    <div className="space-y-3">
                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Choose Your Source</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            Select a DTR book to use its title and content for the cover, or enter custom details.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Enter Cover Details</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            • <strong>Title & Author:</strong> How they'll appear on the cover<br/>
                            • <strong>Genre:</strong> Influences the design style<br/>
                            • <strong>Mood:</strong> Dark, Light, Mysterious, Romantic, etc.<br/>
                            • <strong>Custom Details:</strong> Specific elements you want (e.g., "woman silhouette", "city skyline")
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Configure Back Cover</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            <strong>Auto Summary:</strong> AI generates a blurb from your book<br/>
                            <strong>Author Bio:</strong> Enter your author biography<br/>
                            <strong>Custom:</strong> Write your own back cover text
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">4</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Generate Covers</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            The AI generates 4 unique cover designs. Generation takes 2-5 minutes.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-white border rounded-lg">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold">5</div>
                        <div>
                          <h4 className="font-semibold text-stone-900">Select & Download</h4>
                          <p className="text-stone-600 text-sm mt-1">
                            Choose your favorite design and download high-resolution files for front, back, and spine.
                            Don't like any? Use "Regenerate" (free!) with different details.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-green-900 mb-2">Pricing</h3>
                    <ul className="text-green-800 text-sm space-y-1">
                      <li>• <strong>Create Covers:</strong> 1 credit (generates 4 variations)</li>
                      <li>• <strong>Regenerate:</strong> FREE (unlimited retries)</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Quick Links */}
          <Card className="mt-8">
            <CardContent className="p-6">
              <h3 className="font-semibold text-lg text-stone-900 mb-4">Need More Help?</h3>
              <div className="flex flex-wrap gap-4">
                <Button variant="outline" onClick={() => navigate("/dashboard")}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Dashboard
                </Button>
                <div className="flex items-center gap-2 text-stone-600">
                  <span>Email us:</span>
                  <span className="font-medium select-all">Support@dtrcreative.com</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
